import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { formatPrice } from '../utils/currency';

const TaxManagement = ({ apiClient }) => {
  const [taxComponents, setTaxComponents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingTax, setEditingTax] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const [formData, setFormData] = useState({
    name: '',
    type: 'percentage',
    value: '',
    description: '',
    sign: 'positive'
  });

  useEffect(() => {
    fetchTaxComponents();
    fetchRestaurantCurrency();
  }, []);

  const fetchRestaurantCurrency = async () => {
    try {
      const response = await apiClient.get('/restaurant/profile');
      setCurrency(response.data.currency || 'USD');
    } catch (error) {
      console.error('Error fetching restaurant currency:', error);
      setCurrency('USD');
    }
  };

  const fetchTaxComponents = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/restaurant/taxes');
      setTaxComponents(response.data);
    } catch (error) {
      console.error('Error fetching tax components:', error);
      toast.error('Failed to load tax components');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const finalValue = formData.sign === 'negative' 
        ? -Math.abs(parseFloat(formData.value))
        : Math.abs(parseFloat(formData.value));

      const taxData = {
        name: formData.name,
        type: formData.type,
        value: finalValue,
        description: formData.description
      };

      if (editingTax) {
        await apiClient.put(`/restaurant/taxes/${editingTax.id}`, taxData);
        toast.success('Tax component updated successfully!');
      } else {
        await apiClient.post('/restaurant/taxes', taxData);
        toast.success('Tax component created successfully!');
      }
      
      resetForm();
      fetchTaxComponents();
    } catch (error) {
      console.error('Error saving tax component:', error);
      toast.error('Failed to save tax component');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (tax) => {
    const isNegative = tax.value < 0;
    setFormData({
      name: tax.name,
      type: tax.type,
      value: Math.abs(tax.value).toString(),
      description: tax.description || '',
      sign: isNegative ? 'negative' : 'positive'
    });
    setEditingTax(tax);
    setShowForm(true);
  };

  const handleDelete = async (taxId) => {
    if (!confirm('Are you sure you want to delete this tax component?')) return;

    try {
      await apiClient.delete(`/restaurant/taxes/${taxId}`);
      toast.success('Tax component deleted successfully!');
      fetchTaxComponents();
    } catch (error) {
      console.error('Error deleting tax component:', error);
      toast.error('Failed to delete tax component');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'percentage',
      value: '',
      description: '',
      sign: 'positive'
    });
    setEditingTax(null);
    setShowForm(false);
  };

  return (
    <div className="space-y-6">
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block">
              <div className="spinner w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-gray-600 mt-3">Loading bill settings...</p>
          </div>
        </div>
      )}

      {!loading && (
      <div className="card">
        <div className="card-header">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Bill Management</h2>
            <button
              onClick={() => setShowForm(true)}
              data-testid="add-tax-button"
              className="btn-primary"
            >
              Add Bill Component
            </button>
          </div>
        </div>

        {/* Tax Form */}
        {showForm && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-medium mb-4">
              {editingTax ? 'Edit Bill Component' : 'Add New Bill Component'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="name" className="form-label">
                    Component Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    data-testid="tax-name-input"
                    className="form-input"
                    placeholder="e.g., Sales Tax, Service Charge"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="type" className="form-label">
                    Component Type *
                  </label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    data-testid="tax-type-select"
                    className="form-input"
                    required
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-1">
                    <label htmlFor="sign" className="form-label">
                      Component Sign *
                    </label>
                    <select
                      id="sign"
                      name="sign"
                      value={formData.sign}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    >
                      <option value="positive">Positive (+) - Tax/Fee</option>
                      <option value="negative">Negative (−) - Discount/Rebate</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor="value" className="form-label">
                      {formData.type === 'percentage' ? 'Percentage (%)' : 'Amount ($)'} *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      id="value"
                      name="value"
                      value={formData.value}
                      onChange={handleInputChange}
                      data-testid="tax-value-input"
                      className="form-input"
                      placeholder={formData.type === 'percentage' ? 'e.g., 8.5, 15' : 'e.g., 2.50, 5.00'}
                      required
                    />
                    <div className="mt-1 flex items-center space-x-2">
                      <span className={`text-xs px-2 py-1 rounded ${
                        formData.sign === 'negative' 
                          ? 'bg-red-100 text-red-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {formData.sign === 'negative' ? '🔴 Discount/Rebate' : '🟢 Tax/Fee'}
                      </span>
                      {formData.value && (
                        <span className="text-xs text-gray-600">
                          Preview: {formData.sign === 'negative' ? '-' : '+'}{formData.value}{formData.type === 'percentage' ? '%' : '$'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  💡 <strong>Tip:</strong> Select "Positive" for taxes and fees, "Negative" for discounts and rebates
                  {formData.type === 'percentage' && (
                    <><br />Examples: +8.25% sales tax, -15% senior discount, -10% loyalty discount</>
                  )}
                  {formData.type === 'fixed' && (
                    <><br />Examples: +$2.50 delivery fee, -$5.00 loyalty discount, -$3.00 coupon</>
                  )}
                </p>
              </div>
              <div>
                <label htmlFor="description" className="form-label">
                  Description (Optional)
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  data-testid="tax-description-input"
                  className="form-input"
                  placeholder="Optional description for this bill component"
                  rows="2"
                />
              </div>
              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="save-tax-button"
                  className="btn-primary"
                >
                  {loading ? <div className="spinner"></div> : (editingTax ? 'Update' : 'Create')}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  data-testid="cancel-tax-button"
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Tax Components List */}
        <div className="space-y-4">
          {taxComponents.length === 0 ? (
            <div className="text-center py-8 text-gray-500" data-testid="no-taxes-message">
              No bill components configured. Click "Add Bill Component" to get started.
            </div>
          ) : (
            taxComponents.map((tax) => (
              <div
                key={tax.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                data-testid={`tax-item-${tax.id}`}
              >
                <div className="flex-1">
                  <div className="flex items-center">
                    <h3 className="font-medium text-gray-900" data-testid={`tax-name-${tax.id}`}>
                      {tax.name}
                    </h3>
                    {tax.value < 0 && (
                      <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-800 rounded-full">
                        DISCOUNT
                      </span>
                    )}
                    {tax.value > 0 && (
                      <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full">
                        {tax.type === 'percentage' ? 'TAX' : 'FEE'}
                      </span>
                    )}
                  </div>
                  <p className={`text-sm mt-1 ${tax.value < 0 ? 'text-green-600 font-medium' : 'text-gray-500'}`} data-testid={`tax-details-${tax.id}`}>
                    {tax.type === 'percentage' 
                      ? `${tax.value > 0 ? '+' : ''}${tax.value}% of subtotal${tax.value < 0 ? ' (discount)' : ''}` 
                      : `${tax.value > 0 ? '+' : ''}$${tax.value.toFixed(2)}${tax.value < 0 ? ' (discount)' : ' fee'}`
                    }
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(tax)}
                    data-testid={`edit-tax-${tax.id}`}
                    className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(tax.id)}
                    data-testid={`delete-tax-${tax.id}`}
                    className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Tax Preview */}
        {taxComponents.length > 0 && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Bill Preview (on $100 order)</h4>
            <div className="text-sm text-blue-800 space-y-1">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>$100.00</span>
              </div>
              {taxComponents.map((tax) => {
                const amount = tax.type === 'percentage' ? (100 * tax.value) / 100 : tax.value;
                return (
                  <div key={tax.id} className="flex justify-between">
                    <span>{tax.name}:</span>
                    <span>{formatPrice(amount, currency)}</span>
                  </div>
                );
              })}
              <div className="flex justify-between font-semibold border-t border-blue-300 pt-1">
                <span>Total:</span>
                <span>
                  ${(100 + taxComponents.reduce((total, tax) => 
                    total + (tax.type === 'percentage' ? (100 * tax.value) / 100 : tax.value), 0
                  )).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
        )}
    </div>
  );
};

export default TaxManagement;